/** @module Storage locations: boxes with their compartments and decks, what lies in each. */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  ChevronRight,
  FolderPlus,
  GalleryVerticalEnd,
  Inbox,
  Package,
  Pencil,
  Plus,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { api, problemFrom } from "@/api/client";
import { LocationSelect } from "@/components/location-select";
import { PageHeader } from "@/components/page-header";
import { ComboInput } from "@/components/ui/combo-input";
import { Segmented } from "@/components/ui/controls";
import { Sheet } from "@/components/ui/overlays";
import { count, money } from "@/lib/format";
import { type Location, possibleParents, treeOrder, useLocations } from "@/lib/locations";
import { numberedNames } from "@/lib/suggestions";

type Kind = "box" | "deck";

interface Draft {
  name: string;
  kind: Kind;
  parentId: string | null;
  note: string;
}

/** Base words for new names; each is offered with the next free number. */
function words(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((one): one is string => typeof one === "string") : [];
}

function LocationForm({
  editing,
  parent,
  all,
  onDone,
}: {
  /** Null creates a new place. */
  editing: Location | null;
  /** A new compartment: the box it goes into. */
  parent: string | null;
  all: Location[];
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const client = useQueryClient();
  // Numbers count among neighbours: Box 2 gets its own "Fach 1".
  const namesIn = (parentId: string | null) =>
    all
      .filter((location) => (location.parent_id ?? null) === parentId)
      .map((location) => location.name);
  const compartmentWords = words(t("locations.compartmentWords", { returnObjects: true }));
  const [draft, setDraft] = useState<Draft>(() => ({
    // A compartment starts as "Fach 3", a new place empty, an existing one as it is.
    name:
      editing?.name ?? (parent ? (numberedNames(compartmentWords, namesIn(parent))[0] ?? "") : ""),
    kind: editing?.kind ?? "box",
    parentId: editing?.parent_id ?? parent,
    note: editing?.note ?? "",
  }));
  const parents = (editing ? possibleParents(all, editing.id) : all).filter(
    (location) => location.kind === "box",
  );
  const nameWords =
    draft.kind === "deck"
      ? "locations.deckWords"
      : draft.parentId
        ? "locations.compartmentWords"
        : "locations.boxWords";
  const [confirming, setConfirming] = useState(false);
  const refresh = () => {
    for (const key of ["locations", "collection"]) {
      void client.invalidateQueries({ queryKey: [key] });
    }
    onDone();
  };
  const save = useMutation({
    mutationFn: async () => {
      const body = {
        name: draft.name.trim(),
        kind: draft.kind,
        parent_id: draft.parentId,
        note: draft.note.trim() || null,
      };
      const { data, response } = editing
        ? await api.PATCH("/api/v1/locations/{location_id}", {
            params: { path: { location_id: editing.id } },
            body,
          })
        : await api.POST("/api/v1/locations", { body });
      if (!data) throw problemFrom(response);
      return data;
    },
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: async () => {
      if (!editing) return;
      const { response } = await api.DELETE("/api/v1/locations/{location_id}", {
        params: { path: { location_id: editing.id } },
      });
      if (!response.ok) throw problemFrom(response);
    },
    onSuccess: refresh,
  });

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (draft.name.trim()) save.mutate();
      }}
    >
      <h2 className="t-h3">
        {editing
          ? t("locations.edit")
          : parent
            ? t("locations.addCompartment", {
                name: all.find((location) => location.id === parent)?.name ?? "",
              })
            : t("locations.add")}
      </h2>
      <Segmented
        label={t("locations.kindLabel")}
        value={draft.kind}
        onChange={(kind) => {
          setDraft((current) => ({ ...current, kind }));
        }}
        options={[
          { value: "box", label: t("locations.kind.box") },
          { value: "deck", label: t("locations.kind.deck") },
        ]}
      />
      <ComboInput
        label={t("locations.name")}
        required
        maxLength={80}
        value={draft.name}
        placeholder={draft.kind === "deck" ? t("locations.deckExample") : t("locations.boxExample")}
        onChange={(name) => {
          setDraft((current) => ({ ...current, name }));
        }}
        groups={[
          {
            label: t("suggest.presets"),
            options: numberedNames(
              words(t(nameWords, { returnObjects: true })),
              namesIn(draft.parentId),
            ),
          },
        ]}
      />
      {parents.length > 0 ? (
        <LocationSelect
          label={t("locations.parent")}
          noneLabel={t("locations.topLevel")}
          value={draft.parentId}
          only={parents}
          onChange={(parentId) => {
            setDraft((current) => ({ ...current, parentId }));
          }}
        />
      ) : (
        <p className="t-xs t-muted">{t("locations.noBoxYet")}</p>
      )}
      <ComboInput
        label={t("locations.note")}
        maxLength={500}
        value={draft.note}
        onChange={(note) => {
          setDraft((current) => ({ ...current, note }));
        }}
        groups={[
          {
            label: t("suggest.recent"),
            options: all.flatMap((location) => (location.note ? [location.note] : [])),
          },
          {
            label: t("suggest.presets"),
            options: words(t("locations.noteWords", { returnObjects: true })),
          },
        ]}
      />
      {save.isError || remove.isError ? (
        <p role="alert" className="t-sm loss">
          {t("common.saveError")}
        </p>
      ) : null}
      {confirming ? (
        <div className="flex flex-col gap-3">
          <p className="callout callout-warn">{t("locations.deleteConfirm")}</p>
          <div className="row justify-end gap-2">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => {
                setConfirming(false);
              }}
            >
              {t("collection.bulk.cancel")}
            </button>
            <button
              type="button"
              className="btn btn-danger"
              disabled={remove.isPending}
              onClick={() => {
                remove.mutate();
              }}
            >
              {t("locations.deleteYes")}
            </button>
          </div>
        </div>
      ) : (
        <div className="row gap-2">
          <button
            type="submit"
            className="btn btn-primary grow"
            disabled={save.isPending || !draft.name.trim()}
          >
            {t("common.save")}
          </button>
          {editing ? (
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => {
                setConfirming(true);
              }}
            >
              {t("locations.delete")}
            </button>
          ) : null}
        </div>
      )}
    </form>
  );
}

export function LocationsPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "de";
  const locations = useLocations();
  // A place to edit, or a new one (optionally as a compartment of a box).
  const [editing, setEditing] = useState<
    { location: Location } | { location: null; parent: string | null } | null
  >(null);
  const all = locations.data?.items ?? [];
  const nowhere = locations.data?.without_location;

  const line = (cards: number, value: string, unvalued: number) =>
    [
      t("locations.cards", { count: cards, cards: count(cards, locale) }),
      Number(value) > 0 ? money(value, locale) : null,
      unvalued > 0 ? t("dashboard.unvaluedLine", { count: unvalued }) : null,
    ]
      .filter(Boolean)
      .join(" · ");

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={t("nav.locations")}
        subtitle={locations.data ? t("locations.count", { count: all.length }) : undefined}
        actions={
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              setEditing({ location: null, parent: null });
            }}
          >
            <Plus />
            {t("locations.add")}
          </button>
        }
      />
      {locations.isPending ? <p className="t-muted">{t("common.loading")}</p> : null}
      {locations.isError ? (
        <p role="alert" className="loss">
          {t("common.loadError")}
        </p>
      ) : null}
      {locations.data && all.length === 0 ? (
        <p className="panel panel-pad t-sm t-muted">{t("locations.empty")}</p>
      ) : null}
      {all.length > 0 ? (
        <ul className="panel flex flex-col">
          {treeOrder(all).map(({ location, depth }) => (
            <li
              key={location.id}
              className="row gap-2 border-b border-(--border) pr-2 last:border-0"
              style={{ paddingLeft: `${String(12 + depth * 24)}px` }}
            >
              <Link
                to="/collection"
                search={{ location: location.id }}
                className="row min-h-14 min-w-0 grow gap-3 py-2 text-(--text) no-underline"
              >
                <span className="grid size-9 shrink-0 place-items-center rounded-full bg-(--surface-2) [&_svg]:size-[18px]">
                  {location.kind === "deck" ? <GalleryVerticalEnd /> : <Package />}
                </span>
                <span className="flex min-w-0 grow flex-col">
                  <b className="t-sm truncate">{location.name}</b>
                  <span className="t-xs t-muted num truncate">
                    {line(location.total_count, location.value, location.unvalued)}
                    {location.note ? ` · ${location.note}` : ""}
                  </span>
                </span>
                <ChevronRight className="size-4 shrink-0 text-(--text-muted)" />
              </Link>
              {location.kind === "box" ? (
                <button
                  type="button"
                  className="icon-btn shrink-0"
                  aria-label={t("locations.addCompartment", { name: location.name })}
                  title={t("locations.addCompartment", { name: location.name })}
                  onClick={() => {
                    setEditing({ location: null, parent: location.id });
                  }}
                >
                  <FolderPlus />
                </button>
              ) : null}
              <button
                type="button"
                className="icon-btn shrink-0"
                aria-label={t("locations.editOne", { name: location.name })}
                onClick={() => {
                  setEditing({ location });
                }}
              >
                <Pencil />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {nowhere && nowhere.count > 0 ? (
        <Link
          to="/collection"
          search={{ location: "none" }}
          className="panel panel-pad row gap-3 text-(--text) no-underline"
        >
          <span className="grid size-9 shrink-0 place-items-center rounded-full bg-(--surface-2) [&_svg]:size-[18px]">
            <Inbox />
          </span>
          <span className="flex min-w-0 grow flex-col">
            <b className="t-sm">{t("locations.without")}</b>
            <span className="t-xs t-muted num">
              {line(nowhere.count, nowhere.value, nowhere.unvalued)}
            </span>
          </span>
          <ChevronRight className="size-4 shrink-0 text-(--text-muted)" />
        </Link>
      ) : null}

      <Sheet
        open={editing !== null}
        onClose={() => {
          setEditing(null);
        }}
        label={editing?.location ? t("locations.edit") : t("locations.add")}
      >
        {editing !== null ? (
          <LocationForm
            key={editing.location ? editing.location.id : `new-${editing.parent ?? ""}`}
            editing={editing.location}
            parent={editing.location ? null : editing.parent}
            all={all}
            onDone={() => {
              setEditing(null);
            }}
          />
        ) : null}
      </Sheet>
    </div>
  );
}
